import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { ConnectMomentInteractionThumbnail } from "@/components/conversations/connect-moment-interaction-thumbnail";
import { ConnectMomentResponder } from "@/components/conversations/activities/connect-moment/connect-moment-responder";
import { ConnectMomentResultCard } from "@/components/conversations/connect-moment-result-card";
import { DeepThoughtsInteractionThumbnail } from "@/components/conversations/deep-thoughts-interaction-thumbnail";
import { DeepThoughtsResultCard } from "@/components/conversations/deep-thoughts-result-card";
import { HotTakesInteractionThumbnail } from "@/components/conversations/hot-takes-interaction-thumbnail";
import { HotTakesResponder } from "@/components/conversations/activities/hot-takes/hot-takes-responder";
import { HotTakesResultCard } from "@/components/conversations/hot-takes-result-card";
import { ConnectMomentHubCard } from "@/components/hub/connect-moment/connect-moment-hub-card";
import { ConnectMomentHubView } from "@/components/hub/connect-moment/connect-moment-hub-view";
import { DeepThoughtsHubCard } from "@/components/hub/deep-thoughts";
import { DeepThoughtsHubView } from "@/components/hub/deep-thoughts/deep-thoughts-hub-view";
import { HotTakesHubView } from "@/components/hub/hot-takes/hot-takes-hub-view";
import type { HubView } from "@/components/hub/use-hub-store";
import type { ActivityType } from "@/lib/api/activities";
import type { MentorshipDetails } from "@/lib/api/mentorships";
import type { ShareableEntity } from "@/lib/api/shareable-entities";
import {
  ConnectMomentUnavailableThumbnail,
  DeepThoughtsUnavailableThumbnail,
  HotTakesUnavailableThumbnail,
} from "@/components/shared/entity-thumbnails";
import type { ComponentType } from "react";

export interface ActivityParticipant {
  name?: string;
  avatarUrl: string | null;
}

export interface ActivityParticipants {
  teen: ActivityParticipant;
  mentor: ActivityParticipant;
}

export interface ActivityResultCardProps<TConfig = unknown, TResult = unknown> {
  participants: ActivityParticipants;
  result: TResult;
  config: TConfig;
  onClick: () => void;
}

export interface ActivityInteractionThumbnailProps {
  entityId: string;
  index: number;
  context?: ChatRenderContext;
}

export interface ActivityUnavailableThumbnailProps {
  size?: "sm" | "md";
}

export interface ActivityHubCardProps {
  entity: ShareableEntity;
  isNew: boolean;
  onClick?: () => void;
  className?: string;
}

export interface ActivityHubViewProps {
  entityId: string;
  entity: ShareableEntity | null;
  mentorship: MentorshipDetails;
  mentorAvatarUrl?: string | null;
  teenAvatarUrl?: string | null;
  currentIndex?: number;
  totalEntities?: number;
  isLoadingNextPage?: boolean;
  activeTab?: "new" | "all";
  initialIndex?: number | null;
  onPrevious?: () => void;
  onNext?: () => void;
  onNavigateToList?: () => void;
  readOnly?: boolean;
}

export interface ActivityResponderDialogProps {
  isOpen: boolean;
  entityId: string;
  teenName: string;
  onClose: () => void;
}

export interface ActivityClientConfig {
  /** Fallback display name when availability API labels are unavailable. */
  title: string;
  hubView: HubView;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ResultCard: ComponentType<ActivityResultCardProps<any, any>>;
  InteractionThumbnail: ComponentType<ActivityInteractionThumbnailProps>;
  UnavailableThumbnail: ComponentType<ActivityUnavailableThumbnailProps>;
  HubCard?: ComponentType<ActivityHubCardProps>;
  HubView: ComponentType<ActivityHubViewProps>;
  ResponderDialog?: ComponentType<ActivityResponderDialogProps>;
  featureFlag?: string;
}

/**
 * Single client registry for activities. Adding a type here is the main
 * registration step; Action tools/FAB/history/suggestion launch derive from it.
 */
export const ACTIVITY_REGISTRY: Record<ActivityType, ActivityClientConfig> = {
  hot_takes: {
    title: "Hot takes",
    hubView: "view-hot-takes",
    ResultCard: HotTakesResultCard,
    InteractionThumbnail: HotTakesInteractionThumbnail,
    UnavailableThumbnail: HotTakesUnavailableThumbnail,
    HubView: HotTakesHubView,
    ResponderDialog: HotTakesResponder,
  },
  connect_moment: {
    title: "Connection cards",
    hubView: "view-connect-moment",
    ResultCard: ConnectMomentResultCard,
    InteractionThumbnail: ConnectMomentInteractionThumbnail,
    UnavailableThumbnail: ConnectMomentUnavailableThumbnail,
    HubCard: ConnectMomentHubCard,
    HubView: ConnectMomentHubView,
    ResponderDialog: ConnectMomentResponder,
    featureFlag: "activity_connect_moment",
  },
  deep_thoughts: {
    title: "Deep thoughts",
    hubView: "view-deep-thoughts",
    ResultCard: DeepThoughtsResultCard,
    InteractionThumbnail: DeepThoughtsInteractionThumbnail,
    UnavailableThumbnail: DeepThoughtsUnavailableThumbnail,
    HubCard: DeepThoughtsHubCard,
    HubView: DeepThoughtsHubView,
    featureFlag: "activity_deep_thoughts",
  },
};

/** Stable Action / FAB / History display order. Must cover every ActivityType. */
export const ACTIVITY_TYPES = [
  "hot_takes",
  "connect_moment",
  "deep_thoughts",
] as const satisfies readonly ActivityType[];

type MissingActivityTypes = Exclude<
  ActivityType,
  (typeof ACTIVITY_TYPES)[number]
>;
type AssertAllActivityTypesListed = [MissingActivityTypes] extends [never]
  ? true
  : never;
const _allActivityTypesListed: AssertAllActivityTypesListed = true;
void _allActivityTypesListed;

export function isActivityType(value: string): value is ActivityType {
  return Object.prototype.hasOwnProperty.call(ACTIVITY_REGISTRY, value);
}

export function getActivityConfig(
  type: string,
): ActivityClientConfig | undefined {
  return isActivityType(type) ? ACTIVITY_REGISTRY[type] : undefined;
}

export function getActivityTitle(type: ActivityType): string {
  return ACTIVITY_REGISTRY[type].title;
}

export function findActivityConfigByHubView(
  view: HubView,
): { activityType: ActivityType; config: ActivityClientConfig } | null {
  for (const activityType of ACTIVITY_TYPES) {
    const config = ACTIVITY_REGISTRY[activityType];
    if (config.hubView === view) {
      return { activityType, config };
    }
  }
  return null;
}
